import { useCallback, useEffect, useState } from 'react';
import { List, Title, useListContext, useNotify, useRedirect } from 'react-admin';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, CircularProgress, Autocomplete, Link,
  MenuItem, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import { card, btn, tbl, typo, inputSx, badgeStyle, clickableA11y, NAVY, WEBSITE_LINK_STATE } from '../styles/ds';
import { websiteRequest, listUrl } from './websiteApi';
import authFetch from '../utils/authFetch';

/*
 * Website accounts (Phase D, WD-011): customers who signed up on the website
 * and the CRM customer each is linked to. A link connects, never merges.
 * Linking is automatic when exactly one CRM customer has the same verified
 * email; anything ambiguous waits here for the admin. An account the admin
 * unlinked or whose review was dismissed is never linked automatically again.
 */

const fmtDate = d => (d ? new Date(d).toLocaleDateString('he-IL') : '—');
const fullName = (first, last) => [first, last].filter(Boolean).join(' ');
const PROVIDER = { google: 'Google', email: 'קוד במייל' };
const REVIEW_REASON = {
  multiple_customers: 'כמה לקוחות ב-CRM עם אותו מייל',
  customer_already_linked: 'הלקוח עם המייל הזה כבר מקושר לחשבון אתר אחר',
};
const EVENT = {
  auto_linked: 'קושר אוטומטית', admin_linked: 'קושר ע״י מנהל', unlinked: 'הקישור בוטל',
  review_opened: 'נפתחה בדיקה', review_dismissed: 'הבדיקה נסגרה בלי קישור',
  order_created_customer: 'נוצר לקוח חדש מההזמנה הראשונה',
};
// Notes the server writes itself (websiteCustomerLinks.js); admin notes show as typed.
const EVENT_NOTE = { ...REVIEW_REASON, 'same verified email': 'אותו מייל מאומת' };

const CustomerLink = ({ c }) => (
  <Link component={RouterLink} to={`/customers/${c.id}/show`} onClick={e => e.stopPropagation()}>
    {c.name || c.customer_name || `לקוח #${c.id ?? c.customer_id}`}
  </Link>
);

const Filters = () => {
  const { filterValues, setFilters } = useListContext();
  const [search, setSearch] = useState(filterValues?.q || '');
  const set = (key, value) => setFilters({ ...filterValues, [key]: value || undefined });
  return (
    <Stack className="crm-wrap crm-filter-row" direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
      <TextField size="small" placeholder="חיפוש לפי מייל או שם..." aria-label="חיפוש חשבונות אתר"
        value={search} onChange={e => { setSearch(e.target.value); set('q', e.target.value); }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
          sx: { ...inputSx, direction: 'rtl' },
        }}
        sx={{ width: 340, maxWidth: '100%' }} />
      <TextField select size="small" label="קישור" value={filterValues?.link_state || ''}
        onChange={e => set('link_state', e.target.value)} sx={{ width: 170, backgroundColor: '#fff', borderRadius: 2 }}>
        <MenuItem value="">הכל</MenuItem>
        {Object.entries(WEBSITE_LINK_STATE).map(([id, { label }]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}
      </TextField>
    </Stack>
  );
};

const AccountsTable = () => {
  const { data = [], isPending } = useListContext();
  const redirect = useRedirect();
  if (isPending) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  if (!data.length) return <Typography p={3} color="text.secondary">אין חשבונות להצגה.</Typography>;
  return (
    <TableContainer component={Box} sx={tbl.container}>
      <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="חשבונות אתר"><Table>
        <TableHead><TableRow>
          <TableCell>מייל</TableCell>
          <TableCell>שם</TableCell>
          <TableCell align="center">קישור ל-CRM</TableCell>
          <TableCell>לקוח ב-CRM</TableCell>
          <TableCell align="center">נרשם</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.map((a, i) => (
            <TableRow key={a.id} {...clickableA11y(() => redirect('show', 'website_accounts', a.id), { ariaLabel: `חשבון ${a.contact_email}` })}
              onClick={() => redirect('show', 'website_accounts', a.id)} sx={tbl.bodyRow(i)}>
              <TableCell><Typography fontSize={14} dir="ltr" textAlign="right">{a.contact_email}</Typography></TableCell>
              <TableCell>{fullName(a.first_name, a.last_name) || '—'}</TableCell>
              <TableCell align="center">
                <span style={badgeStyle(WEBSITE_LINK_STATE, a.link_state)}>{WEBSITE_LINK_STATE[a.link_state]?.label}</span>
              </TableCell>
              <TableCell>{a.customer_id ? <CustomerLink c={a} /> : '—'}</TableCell>
              <TableCell align="center">{fmtDate(a.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Box>
    </TableContainer>
  );
};

export const WebsiteAccountList = () => (
  <List title="חשבונות אתר" actions={false} storeKey={false} sort={{ field: 'created_at', order: 'DESC' }}
    sx={tbl.listSx} className="crm-mobile-list crm-inline-list">
    <Typography variant="body2" color="text.secondary" mb={2}>
      לקוחות שנרשמו באתר, והלקוח ב-CRM שכל חשבון מקושר אליו. קישור לא ממזג רשומות. "לבדיקה" — מקרים שהמערכת לא קישרה לבד.
    </Typography>
    <Filters />
    <AccountsTable />
  </List>
);

// ── Account page ───────────────────────────────────────────────────────────

/** Admin action with a confirmation dialog and an optional/required note. */
const ActionDialog = ({ title, text, noteLabel, noteRequired, confirm, onClose, onConfirm, busy }) => {
  const [note, setNote] = useState('');
  return (
    <Dialog open onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography mb={2}>{text}</Typography>
        <TextField label={noteLabel} value={note} onChange={e => setNote(e.target.value)} multiline minRows={2}
          inputProps={{ maxLength: 500 }} sx={inputSx} fullWidth autoFocus />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={btn.cancel}>ביטול</Button>
        <Button variant="contained" disabled={busy || (noteRequired && !note.trim())} onClick={() => onConfirm(note)} sx={btn.primary}>{confirm}</Button>
      </DialogActions>
    </Dialog>
  );
};

const CustomerPicker = ({ onPick, disabled }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (input.trim().length < 2) { setOptions([]); return undefined; }
    const t = setTimeout(() => {
      authFetch(listUrl('customers', { q: input.trim() }, 20))
        .then(r => r.json()).then(rows => setOptions(Array.isArray(rows) ? rows : []))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [input]);
  return (
    <Autocomplete size="small" disabled={disabled} options={options} filterOptions={x => x}
      getOptionLabel={c => `${fullName(c.first_name, c.last_name) || `לקוח #${c.id}`}${c.email ? ` · ${c.email}` : ''}`}
      onInputChange={(_, v) => setInput(v)} onChange={(_, c) => c && onPick(c)}
      noOptionsText={input.trim().length < 2 ? 'הקלידו שם, מייל או טלפון' : 'לא נמצאו לקוחות'}
      renderInput={params => <TextField {...params} label="קישור ידני ללקוח ב-CRM" sx={inputSx} />} />
  );
};

const CandidateTable = ({ rows, onLink, disabled }) => (
  <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="לקוחות מתאימים"><Table size="small">
    <TableHead><TableRow>
      <TableCell /><TableCell>לקוח</TableCell><TableCell>מייל</TableCell><TableCell>טלפון</TableCell>
      <TableCell align="center">הזמנות</TableCell>
    </TableRow></TableHead>
    <TableBody>
      {rows.map(c => (
        <TableRow key={c.id}>
          <TableCell><Button size="small" variant="outlined" disabled={disabled} onClick={() => onLink(c)}>קישור</Button></TableCell>
          <TableCell><CustomerLink c={c} /></TableCell>
          <TableCell dir="ltr" sx={{ textAlign: 'right' }}>{c.email || '—'}</TableCell>
          <TableCell dir="ltr" sx={{ textAlign: 'right' }}>{c.phone || '—'}</TableCell>
          <TableCell align="center">{c.orders_count}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table></Box>
);

export const WebsiteAccountShow = () => {
  const { id } = useParams();
  const notify = useNotify();
  const redirect = useRedirect();
  const [account, setAccount] = useState(null);
  const [dialog, setDialog] = useState(null);     // { kind: 'link' | 'unlink' | 'dismiss', customer? }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    websiteRequest(`/website_accounts/${id}`).then(setAccount).catch(err => notify(err.message, { type: 'error' }));
  }, [id, notify]);
  useEffect(() => { load(); }, [load]);
  if (!account) return <Box p={6} textAlign="center"><CircularProgress /></Box>;

  const run = async note => {
    setBusy(true);
    try {
      const path = { link: 'link', unlink: 'unlink', dismiss: 'dismiss_review' }[dialog.kind];
      const body = dialog.kind === 'link' ? { customer_id: dialog.customer.id, note } : { note };
      setAccount(await websiteRequest(`/website_accounts/${id}/${path}`, 'POST', body));
      notify({ link: 'החשבון קושר ללקוח', unlink: 'הקישור בוטל', dismiss: 'הבדיקה נסגרה' }[dialog.kind], { type: 'success' });
      setDialog(null);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };
  const state = account.link ? 'linked' : account.open_review ? 'review' : 'none';

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Title title={`חשבון אתר: ${account.contact_email}`} />
      <Button startIcon={<ArrowForwardIcon />} onClick={() => redirect('list', 'website_accounts')} sx={{ mb: 1 }}>חזרה לחשבונות האתר</Button>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
            <Typography sx={typo.sectionTitle} dir="ltr">{account.contact_email}</Typography>
            <span style={badgeStyle(WEBSITE_LINK_STATE, state)}>{WEBSITE_LINK_STATE[state].label}</span>
            {account.disabled_at && <Typography color="error" fontSize={13}>החשבון מושבת</Typography>}
          </Stack>
          <Stack spacing={0.5} fontSize={14}>
            <Typography fontSize={14}>שם: {fullName(account.first_name, account.last_name) || '—'}</Typography>
            <Typography fontSize={14}>טלפון: <bdi dir="ltr">{account.phone || '—'}</bdi></Typography>
            <Typography fontSize={14}>נרשם: {fmtDate(account.created_at)}</Typography>
            <Typography fontSize={14}>כניסה באמצעות: {account.identities.map(i => PROVIDER[i.provider]).join(', ') || '—'}</Typography>
          </Stack>
          <Typography fontSize={12} color="text.secondary" mt={1.5}>
            פרטים שהלקוח מעדכן באתר לא משנים את כרטיס הלקוח ב-CRM.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ ...typo.sectionTitle, mb: 2 }}>קישור ללקוח ב-CRM</Typography>
          {account.link && (
            <Stack spacing={1.5}>
              <Typography>
                מקושר ל-<CustomerLink c={account.link} /> ({account.link.linked_by === 'auto' ? 'אוטומטית, לפי מייל מאומת' : 'ע״י מנהל'}, {fmtDate(account.link.linked_at)})
              </Typography>
              <Box><Button variant="outlined" color="error" onClick={() => setDialog({ kind: 'unlink' })}>ביטול הקישור</Button></Box>
            </Stack>
          )}
          {account.open_review && (
            <Stack spacing={1.5}>
              <Alert severity="warning">לבדיקה: {REVIEW_REASON[account.open_review.reason] ?? account.open_review.reason}. יש לבחור את הלקוח הנכון, או לסגור בלי קישור.</Alert>
              <CandidateTable rows={account.open_review.candidates} disabled={busy} onLink={c => setDialog({ kind: 'link', customer: c })} />
              <Box><Button variant="outlined" onClick={() => setDialog({ kind: 'dismiss' })}>סגירה בלי קישור</Button></Box>
            </Stack>
          )}
          {!account.link && (
            <Box mt={account.open_review ? 3 : 0} maxWidth={460}>
              {!account.open_review && <Typography color="text.secondary" mb={1.5}>החשבון לא מקושר ללקוח ב-CRM.</Typography>}
              <CustomerPicker disabled={busy} onPick={c => setDialog({ kind: 'link', customer: { ...c, name: fullName(c.first_name, c.last_name) } })} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ ...typo.sectionTitle, mb: 1 }}>היסטוריית קישור</Typography>
          {account.events.length === 0 ? <Typography color="text.secondary">אין.</Typography> : account.events.map((e, i) => (
            <Typography key={i} fontSize={13} py={0.5}>
              {fmtDate(e.created_at)} — {EVENT[e.event] ?? e.event}{e.customer_id ? ` (לקוח #${e.customer_id})` : ''}{e.actor ? ` · ${e.actor}` : ''}{e.note ? ` · ${EVENT_NOTE[e.note] ?? e.note}` : ''}
            </Typography>
          ))}
        </CardContent>
      </Card>

      {dialog?.kind === 'link' && (
        <ActionDialog title="קישור ללקוח" busy={busy} confirm="קישור" noteLabel="הערה (רשות)"
          text={`לקשר את החשבון ${account.contact_email} ללקוח "${dialog.customer.name || `#${dialog.customer.id}`}"? הרשומות לא ימוזגו.`}
          onClose={() => setDialog(null)} onConfirm={run} />
      )}
      {dialog?.kind === 'unlink' && (
        <ActionDialog title="ביטול קישור" busy={busy} confirm="ביטול הקישור" noteLabel="סיבה" noteRequired
          text="החשבון לא יקושר שוב אוטומטית. אפשר לקשר אותו ידנית בהמשך."
          onClose={() => setDialog(null)} onConfirm={run} />
      )}
      {dialog?.kind === 'dismiss' && (
        <ActionDialog title="סגירת הבדיקה" busy={busy} confirm="סגירה" noteLabel="הערה (רשות)"
          text="החשבון יישאר בלי קישור, ולא יקושר אוטומטית בעתיד. אפשר לקשר אותו ידנית בהמשך."
          onClose={() => setDialog(null)} onConfirm={run} />
      )}
    </Box>
  );
};
